// Check if a listing with a specific size is in the cart
/**
 * @param {Object} param0
 * @param {Object} param0.cart
 * @param {string} param0.providerId
 * @param {string} param0.listingId
 * @param {string} param0.size - Optional size to check for
 * @returns {boolean}
 */
export const isListingWithSizeInCart = ({ cart, providerId, listingId, size }) => {
  // Early return for invalid inputs
  if (!cart || !providerId || !listingId) {
    return false;
  }

  // Direct lookup instead of iterating through all providers
  const providerCart = cart[providerId];
  if (!providerCart) {
    return false;
  }

  const listing = providerCart[listingId];
  if (!listing?.sizes) {
    return false;
  }
  if (!size) {
    return Object.keys(listing.sizes).length > 0;
  }

  return listing.sizes[size] > 0;
};

/**
 * Get the total count of items in the cart
 * @param {Object} cart
 * @returns {number}
 */
export const getCartCount = cart => {
  if (!cart || typeof cart !== 'object') {
    return 0;
  }

  return Object.values(cart).reduce((totalCount, providerCart) => {
    if (!providerCart || typeof providerCart !== 'object') {
      return totalCount;
    }

    return (
      totalCount +
      Object.values(providerCart).reduce((providerCount, listing) => {
        if (!listing?.sizes) {
          return providerCount;
        }

        return (
          providerCount +
          Object.values(listing.sizes).reduce((sizeCount, quantity) => {
            return sizeCount + (Number(quantity) || 0);
          }, 0)
        );
      }, 0)
    );
  }, 0);
};

/**
 * Remove an item from the cart
 * @param {Object} cart
 * @param {string} providerId
 * @param {string} listingId
 * @param {string} size
 * @returns {Object}
 */
const removeItemFromCart = (cart, providerId, listingId, size) => {
  const newCart = { ...cart };

  if (!newCart[providerId]?.[listingId]) {
    return newCart;
  }

  const newSizes = { ...newCart[providerId][listingId].sizes };
  delete newSizes[size];

  if (Object.keys(newSizes).length === 0) {
    delete newCart[providerId][listingId];
  } else {
    newCart[providerId][listingId].sizes = newSizes;
  }

  // Remove provider if no more items
  if (Object.keys(newCart[providerId]).length === 0) {
    delete newCart[providerId];
  }

  return newCart;
};

/**
 * Add or update an item in the cart
 * @param {Object} cart
 * @param {string} providerId
 * @param {string} listingId
 * @param {string} size
 * @param {number} quantity
 * @returns {Object}
 */
const addOrUpdateItemInCart = (cart, providerId, listingId, size, quantity) => {
  return {
    ...cart,
    [providerId]: {
      ...cart[providerId],
      [listingId]: {
        ...(cart[providerId]?.[listingId] || {}),
        sizes: {
          ...(cart[providerId]?.[listingId]?.sizes || {}),
          [size]: quantity,
        },
      },
    },
  };
};

/**
 * Update an item in the cart
 * @param {Object} cart
 * @param {string} providerId
 * @param {string} listingId
 * @param {Object} quantityAndSize - Object with quantity and size keys { quantity: 1, size: 'S' }
 * @returns {Object}
 */
export const updateCart = (cart, providerId, listingId, { quantity, size }) => {
  // Validate inputs
  if (!providerId || !listingId || !size) {
    console.warn('updateCart: Missing required parameters', { providerId, listingId, size });
    return cart;
  }

  // If quantity is 0 or falsy, remove the item
  if (!quantity || quantity <= 0) {
    return removeItemFromCart(cart, providerId, listingId, size);
  }

  // Add or update item with positive quantity
  return addOrUpdateItemInCart(cart, providerId, listingId, size, quantity);
};

/**
 * Get the quantity of an item in the cart
 * @param {Object} cart
 * @param {string} providerId
 * @param {string} listingId
 * @param {string} size
 * @returns {number}
 */
export const getCartItemQuantity = (cart, providerId, listingId, size) => {
  if (!cart?.[providerId]?.[listingId]?.sizes?.[size]) {
    return 0;
  }
  return cart[providerId][listingId].sizes[size];
};

export const getCartTotalItems = cart => {
  if (!cart || typeof cart !== 'object') {
    return 0;
  }

  return Object.values(cart).reduce((total, providerCart) => {
    if (!providerCart || typeof providerCart !== 'object') {
      return total;
    }

    return (
      total +
      Object.values(providerCart).reduce((providerTotal, listing) => {
        if (!listing?.sizes) {
          return providerTotal;
        }

        return (
          providerTotal +
          Object.values(listing.sizes).reduce((sizeTotal, quantity) => {
            return sizeTotal + (Number(quantity) || 0);
          }, 0)
        );
      }, 0)
    );
  }, 0);
};

/**
 * Clear the cart
 * @returns {Object}
 */
export const clearCart = () => {
  return {};
};

/**
 * Remove a listing from the cart
 * @param {Object} cart
 * @param {string} providerId
 * @param {string} listingId
 * @returns {Object}
 */
export const removeListingFromCart = (cart, providerId, listingId) => {
  if (!cart?.[providerId]?.[listingId]) {
    return cart;
  }

  const newCart = { ...cart };
  delete newCart[providerId][listingId];

  // Remove provider if no more items
  if (Object.keys(newCart[providerId]).length === 0) {
    delete newCart[providerId];
  }

  return newCart;
};

/**
 * Update cart with multiple sizes and quantities for a single listing
 * This is used when the user selects multiple sizes with different quantities
 * @param {Object} cart - Current cart object
 * @param {string} providerId - Provider ID
 * @param {string} listingId - Listing ID
 * @param {Object} orderedSizeWithQuantities - Object with size keys and quantity values { "S": 2, "M": 1, "L": 3 }
 * @returns {Object} New cart object
 */
export const updateCartWithSizeQuantities = (
  cart,
  providerId,
  listingId,
  orderedSizeWithQuantities
) => {
  if (!cart || !providerId || !listingId || !orderedSizeWithQuantities) {
    return cart;
  }

  let newCart = { ...cart };

  // Remove any existing sizes for this listing that are not in the new selection
  if (newCart[providerId]?.[listingId]?.sizes) {
    const existingSizes = Object.keys(newCart[providerId][listingId].sizes);
    const newSizes = Object.keys(orderedSizeWithQuantities);

    existingSizes.forEach(size => {
      if (!newSizes.includes(size)) {
        delete newCart[providerId][listingId].sizes[size];
      }
    });
  }

  // Update or add new sizes and quantities
  Object.entries(orderedSizeWithQuantities).forEach(([size, quantity]) => {
    if (quantity > 0) {
      newCart = updateCart(newCart, providerId, listingId, { quantity, size });
    }
  });

  // Clean up empty listings and providers
  if (newCart[providerId]?.[listingId]?.sizes) {
    if (Object.keys(newCart[providerId][listingId].sizes).length === 0) {
      delete newCart[providerId][listingId];
    }
  }

  if (newCart[providerId] && Object.keys(newCart[providerId]).length === 0) {
    delete newCart[providerId];
  }
  return newCart;
};

/**
 * Get the size options for a listing
 * @param {string[]} sizes
 * @param {Object[]} sizeEnumOptions
 * @param {Object} sizesWithQuantity
 * @returns {Object[]}
 */
export const getSizeOptions = (sizes, sizeEnumOptions, sizesWithQuantity = {}) => {
  if (!sizeEnumOptions || !sizes) {
    return [];
  }
  return sizes.map(size => {
    const sizeConfig = sizeEnumOptions.find(c => c.option === size);
    return {
      key: size,
      label: sizeConfig.label,
      quantity: sizesWithQuantity[size] || 0,
    };
  });
};
